<style lang="postcss">
	.box {
		font-size: 92.5%;
		border-radius: 0.2rem;
		background-color: rgb(219, 219, 219);
		padding: 0 0 0 0.375rem;
	}
	.select-component {
		display: flex;
		flex-direction: column;
		align-items: stretch;
		justify-content: flex-start;
	}
	.select-component-button {
		justify-content: space-between;
		position: relative;
		display: flex;
		flex-direction: row;
		flex-wrap: nowrap;
		align-items: center;
	}
	.select-component.is-sm .select-component-button-inner {
		margin-left: -0.25rem;
	}
	.select-component-button-inner {
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		flex: 1 1 auto;
		min-width: 0;
		text-align: left;
		row-gap: 0.25rem;
	}
	.select-component-button:focus {
		outline: none;
	}
	.selected-item {
		display: inline-flex;
		max-width: 100%;
		flex-direction: row;
		align-items: center;
		will-change: auto;
	}
	.remove-item {
		display: flex;
		max-height: 100%;
		width: 1.25rem;
		flex: none;
		cursor: pointer;
		align-items: center;
		justify-content: center;
		align-self: stretch;
		font-size: 0.75rem;
		font-weight: 600;
		color: rgb(200, 78, 78);
		opacity: 0.7;
	}
	.remove-item:hover {
		opacity: 1;
	}
	.select-input {
		display: inline-block;
		align-self: center;
		border-radius: 0.25rem;
		min-width: 1px;
	}
	.select-input.focus:empty::before {
		opacity: 0.6;
	}
	.select-input:empty::before {
		opacity: 0.4;
	}
	.select-input:focus {
		outline: none;
	}
	.select-input:empty::before {
		content: attr(placeholder);
	}
	.select-input.absolute {
		position: absolute;
		width: 100%;
	}
	.chevron-down {
		display: flex;
		align-items: center;
		justify-content: center;
		align-self: center;
		padding: 0.125rem;
	}
	.group-chevron {
		padding: 2px;
		border-radius: 2px;
	}
	.x-mark {
		display: flex;
		cursor: pointer;
		align-items: center;
		justify-content: center;
		align-self: center;
		padding: 0.125rem;
		color: rgb(200, 78, 78);
		opacity: 0.7;
	}
	.x-mark:hover {
		opacity: 1;
	}
	.select-dropdown-cover {
		display: flex;
		flex-flow: column nowrap;
		position: absolute;
		z-index: 50;
		border-radius: 0.25rem;
		background-color: var(--solid-background-color, white);
		min-height: 2.1rem;
		will-change: transform;
		overflow-behaviour: contain;
		width: max-content;
		min-width: 12rem;
	}
	.select-dropdown {
		display: flex;
		flex-direction: column;
		overflow-y: auto;
		user-select: none;
		width: 100%;
	}
	.select-dropdown-footer {
		font-size: 10px;
		opacity: 0.4;
		padding: 0.17rem 0.75rem;
		text-align: right;
		font-style: italic;
	}
	.loading-cover {
		background-color: var(--solid-background-color, rgba(255, 255, 255, 0.8));
		position: absolute;
		left: 0;
		top: 0;
		width: 100%;
		height: 100%;
		pointer-events: none;
		display: flex;
		flex-direction: row;
		align-items: center;
		z-index: 50;
		font-size: 95%;
	}
	.group-item {
		display: flex;
		flex-direction: column;
	}
	.group-item.first {
		border-top: 0;
		padding-top: 0;
	}
	.group-item.border-top {
		margin-top: 0.5rem;
		border-top: 1px solid rgba(0, 0, 0, 0.15);
	}
	:not(.multi-column) .group-item.last {
		padding-bottom: 0.25rem;
	}
	.select-item-head {
		text-align: left;
		padding: 0 0.75rem 0.1rem 0.75rem;
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding-top: 0.4rem;
	}
	.select-item-head:hover .group-chevron,
	.select-item-head.active .group-chevron {
		background: rgba(0, 0, 0, 0.05);
	}
	.select-item-head-text {
		display: flex;
		flex-flow: row nowrap;
		align-items: center;
		gap: 0.35rem;
	}
	.select-item-head-pill {
		background-color: rgba(0, 0, 0, 0.2);
		font-size: 11px;
		border-radius: 3px;
		padding: 0px 4px;
		margin-right: 8px;
		font-weight: 500;
		opacity: 0.8;
	}
</style>

<script lang="ts">
	import {
		computePosition,
		flip,
		offset as offsetModifier,
		autoUpdate,
		size as sizeModifier
	} from "@floating-ui/dom";
	import { getImageSrc } from "$lib/helpers/functions";
	import pkg from "flexsearch";
	import InfiniteLoading from "$basic/InfiniteLoading.svelte";
	const { Index } = pkg;
	import equal from "fast-deep-equal";
	import { portal } from "design/helpers/svelte-portal";
	import SelectEmbed from "./SelectEmbed.svelte";
	import SelectItem from "./SelectItem.svelte";
	import { mousedownOutside } from "$lib/actions";
	import { BROWSER } from "esm-env";
	import {
		createEventDispatcher,
		onDestroy,
		onMount,
		tick,
		getContext
	} from "svelte";
	import Label from "$basic/Label.svelte";
	import { debounce } from "$lib/helpers/functions";
	import { isNumeric } from "$lib/helpers/math";
	import { subtlepop } from "$lib/transitions/pop";
	import { shift } from "svelte-floating-ui/core";
	import type { t as tBase } from "$lib/helpers/translate";

	const dispatch = createEventDispatcher();
	const t = getContext<typeof tBase>("t");

	// Imported Config
	export let footer: string = "";
	export let tabindex = 0;
	export let options: ChoiceOption[] = [];
	export let optionsFn:
		| ((
				skip: number | null,
				take: number | null,
				search: string
		  ) => Promise<{
				total: number | null;
				data: ChoiceOption[];
				complete?: boolean;
				error?: string | null;
		  }>)
		| null = null;
	export let max: number = 1;
	export let returnValue: boolean = false;
	export let placeholder: string = "(select)";
	export let uncheckOnClick: boolean = true;
	export let label: string = "";
	export let fixedWidth: number | null = null;
	export let maxWidth: string | null = null;
	export let chevron: boolean = false;
	export let chevronSize: number = 12;
	export let cross: boolean = true;
	export let columns: number = 1;
	export let remotePagination: boolean = false;
	export let remoteSearch: boolean = false;
	export let truncate: boolean = false;
	export let positionRef: HTMLElement | null = null;
	export let remoteTake: number = 25;
	export let offset: number = 6;
	export let element: HTMLDivElement = {} as HTMLDivElement;
	export let textSize: "sm" | "base" = "sm";
	export let optionsLabels: ChoiceOption[] = [];
	export let description: string = "";
	export let instructions: string = "";
	export let animate = false;
	export let fancyPlacement = true;
	export let showAllGroups = true;
	export let alwaysShowGroups = false;
	export let suffixIcon: any = null;

	// Shared
	let internalValue: ChoiceOption[] | string[] = [];
	let allowSearch: boolean = false;
	let clearButton: HTMLButtonElement | null = null;
	let isRemote: boolean = optionsFn !== null;
	let remoteLoading: number = 0;
	let willLoad: boolean = false;
	let remoteTotal: number | null = null;
	let listVisible: boolean = true;
	let highlightText: string = "";
	let initialFetch = false;
	let noResultsDiv: HTMLDivElement;
	let hasDescription: boolean = false;
	let listHeightSimple: number = 0;
	let openGroups: string[] = [];

	export { internalValue as value };
	export let first: string | ChoiceOption | undefined = returnValue
		? ""
		: undefined;

	// Internal
	let cleanup: CallbackFn;
	let items: AllItem[] = [];
	let groups: string[] | null = null;
	let selectedItems: SelectedItem[] = getInitialSelectedItems();
	let open: boolean = false;
	let inputFocus: boolean = false;
	let listboxFocus: boolean = false;
	let searchText: string = "";
	let inputRef: HTMLSpanElement;
	let listboxRef: HTMLDivElement;
	let listboxOptionsRef: HTMLDivElement;
	let hasHover: boolean = false;
	let selectRef: HTMLButtonElement | null = null;
	let computedPlaceholder: string = placeholder;
	let showMark: boolean = false;
	let index: any;
	let optionsFromFunction: ChoiceOption[] = [];
	let infinite: InfiniteState;
	let scrollToTop: CallbackFn;
	let start: number;
	let end: number;
	let virtualListElement: HTMLElement;
	let virtualListCover: HTMLDivElement;
	let infiniteStatus: number = 0;
	let deep_shadow: boolean = false;

	// Fixed
	const otherGroup = "Others";
	const nf = new Intl.NumberFormat();

	$: changeValue(internalValue);

	function changeValue(internal: ChoiceValue[] | string[]) {
		if (returnValue === false && isRemote === false && first === undefined) {
			const internalLabels = internal.map((x) => x.label);
			const selectedLabels = selectedItems.map((x) => x.label);
			if (equal(internalLabels, selectedLabels) === false) {
				selectedItems = (internal as SelectedItem[]).map((x) => ({
					id: `${x.label}${x.value}`,
					label: x.label,
					value: x.value
				}));
			}
		}
	}

	$: showMark =
		(open || hasHover) && max === 1 && selectedItems.length > 0 && cross;

	$: {
		selectedItems;
		dispatch("update", {
			value: selectedItems.map(({ label, value }) => ({ label, value }))
		});
	}

	$: {
		options;
		if (!isRemote) {
			receivedNewProps(options);
			if (options.length > 12) {
				allowSearch = true;
			} else {
				allowSearch = false;
			}
		}
	}

	$: {
		if (isRemote) {
			optionsFromFunction;
			receivedNewPropsFunction(optionsFromFunction);
			allowSearch = true;
		}
	}

	$: updateSearchText(searchText);
	$: updateSelectedItems(selectedItems);

	function getGroups(options: ChoiceOption[]): string[] {
		if (!options || !Array.isArray(options)) {
			return [];
		}
		const groups = Array.from(
			new Set(options.map((x) => x.group || otherGroup))
		);
		return groups;
	}

	const receivedNewProps = (options: ChoiceOption[]) => {
		groups = getGroups(options);
		if (groups.length) {
			// openGroups = [groups[0]];
		}
		const hasGroups = alwaysShowGroups ? groups.length > 1 : groups.length > 1;

		hasDescription = options.find((x) => x.description && x.description !== "")
			? true
			: false;
		items = options
			? options.map((x, y) => {
					return {
						...x,
						group: hasGroups ? x.group || otherGroup : undefined,
						id: `${x.label}${x.value}`,
						index: y,
						visible: true,
						focus: false
					};
				})
			: [];

		// Sort items based on groups
		if (groups.length > 1) {
			items = items.sort((a, b) => {
				const groupA = a.group || otherGroup;
				const groupB = b.group || otherGroup;
				const indexA = groups!.indexOf(groupA);
				const indexB = groups!.indexOf(groupB);
				return indexA - indexB;
			});
		}

		if (selectedItems) {
			selectedItems = selectedItems.filter(Boolean).map((x) => {
				const found = items.find((y) =>
					x.label === "" ? y.value === x.value : y.id === x.id
				);
				return {
					id: `${found?.label || x.label}${x.value}`,
					label: found?.label || x.label,
					value: x.value,
					embed: found?.embed
				};
			});
		}
		index = new Index({
			preset: "default",
			tokenize: "full",
			cache: false,
			resolution: 9
		});
		Object.values(items).forEach((item, i) => {
			index.add(item.label, item.label);
		});
	};

	function receivedNewPropsFunction(options: ChoiceOption[]) {
		groups = getGroups(options);
		const hasGroups = alwaysShowGroups ? groups.length > 0 : groups.length > 1;
		hasDescription = options.find((x) => x.description && x.description !== "")
			? true
			: false;

		items = options
			? options.map((x, y) => {
					return {
						...x,
						group: hasGroups ? x.group || otherGroup : undefined,
						id: `${x.label}${x.value}`,
						index: y,
						visible: true,
						focus: false
					};
				})
			: [];

		// Sort items based on groups
		if (groups.length > 1) {
			items = items.sort((a, b) => {
				const groupA = a.group || otherGroup;
				const groupB = b.group || otherGroup;
				const indexA = groups!.indexOf(groupA);
				const indexB = groups!.indexOf(groupB);
				return indexA - indexB;
			});
		}
		if (!remoteSearch) {
			index = new Index({
				preset: "default",
				tokenize: "forward",
				cache: false,
				resolution: 9
			});
			Object.values(items).forEach((item, i) => {
				index.add(item.label, item.label);
			});
			updateSearchText(searchText);
		}
	}

	// Convert received value into internal representation
	function getInitialSelectedItems() {
		let result: any = [];

		if (first) {
			// From one item
			if (returnValue) {
				// From string
				const label = optionsLabels.find((y) => first === y.value)?.label ?? "";
				result = [{ label, id: first, value: first }];
			} else if (typeof first !== "string") {
				// From object
				result = [
					{
						label: first.label,
						id: first.value,
						value: first.value
					}
				];
			}
		} else {
			// From array
			if (returnValue) {
				// From array of strings
				result = (internalValue as SelectedItem[]).filter(Boolean).map((x) => {
					const find =
						typeof x === "string"
							? optionsLabels.find((y) => x === y.value)
							: { label: "", value: "" };
					return {
						label: find?.label ?? "",
						id: x,
						value: x
					};
				});
			} else {
				// From array of objects
				result = (internalValue as SelectedItem[]).map((x) => ({
					id: `${x.label}${x.value}`,
					label: x.label,
					value: x.value
				}));
			}
		}

		return result;
	}

	$: {
		inputFocus, listboxFocus, allowSearch;
		if (BROWSER) {
			requestAnimationFrame(() => {
				changeFocus();
			});
		}
	}

	const updateSelectedItems = (selectedItems: SelectedItem[]) => {
		if (selectedItems) {
			// Update return (value)
			let tempInternalValue;
			if (returnValue) {
				tempInternalValue = selectedItems.map(({ value }) => value);
			} else {
				tempInternalValue = selectedItems.map(({ label, value }) => ({
					label,
					value
				}));
			}
			if (equal(internalValue, tempInternalValue) === false) {
				internalValue = tempInternalValue;
			}

			// Update return (first)
			if (returnValue) {
				first = selectedItems[0]?.value || "";
			} else {
				first =
					selectedItems.length > 0
						? {
								label: selectedItems[0].label,
								value: selectedItems[0].value
							}
						: undefined;
			}
		}
	};

	const changeFocus = () => {
		if (!(inputFocus || listboxFocus)) {
			closeListBox();
		}
	};

	const selectThisItem = (selectedItem: SelectedItem) => {
		const previousLength = selectedItems.length;
		let newSelectedItems = [];

		// Remove item if already selected
		newSelectedItems = uncheckOnClick
			? selectedItems.filter(
					({ label, value }) =>
						!equal(
							{ value: value, label: label },
							{ value: selectedItem.value, label: selectedItem.label }
						)
				)
			: [...selectedItems];

		if (max > 1 && newSelectedItems.length === max) {
			return;
		}
		if (previousLength === newSelectedItems.length) {
			newSelectedItems.push(selectedItem);
			newSelectedItems.splice(0, newSelectedItems.length - max);
		}
		if (newSelectedItems.length === max) {
			if (inputRef) inputRef.blur();
			closeListBox();
		}
		selectedItems = [...newSelectedItems];
	};

	function findItemAfterId(array: AllItem[], id: string, skip: number) {
		let itemIndex = -1;
		const enabledItems = array.filter((x) => !x.disabled);
		for (let i = 0; i < enabledItems.length; i++) {
			if (enabledItems[i].id === id) {
				itemIndex = i;
				break;
			}
		}
		if (itemIndex === -1) {
			return null;
		}
		let newIndex = (itemIndex + skip) % enabledItems.length;
		if (newIndex < 0) {
			newIndex += enabledItems.length;
		}
		return enabledItems[newIndex].id;
	}

	// To prevent a newline
	const inputRefKeydown = (event: KeyboardEvent) => {
		if (event.key === "Enter") {
			event.preventDefault();
		}
	};

	const keydown = (event: KeyboardEvent) => {
		if (event.key === "Enter") {
			event.preventDefault();
			event.stopImmediatePropagation();
			if (remoteLoading) return;
			const selectedItem = items.find((x) => x.focus);
			if (!selectedItem) return;
			searchText = "";
			selectThisItem(selectedItem);
			if (max === 1 && inputRef) inputRef.blur();
		}
		if (event.key === "ArrowDown") {
			event.preventDefault();
		}
		if (event.key === "ArrowUp") {
			event.preventDefault();
		}
	};

	const keyup = (event: KeyboardEvent) => {
		const visibleItems = items.filter((x) => x.visible);
		let newSelectedId: string | null = null;
		let selectedId = items.find((x) => x.focus)?.id;
		if (items.length === 0) return;

		if (typeof selectedId === "undefined") {
			selectedId = items[items.length - 1].id;
		}

		// if (event.key === "Enter") {
		// 	event.preventDefault();
		// 	event.stopImmediatePropagation();
		// 	if (remoteLoading) return;
		// 	const selectedItem = items.find((x) => x.focus);
		// 	if (!selectedItem) return;
		// 	searchText = "";
		// 	selectThisItem(selectedItem);
		// 	if (max === 1 && inputRef) inputRef.blur();
		// }

		if (event.key === "ArrowDown") {
			event.preventDefault();
			newSelectedId = findItemAfterId(visibleItems, selectedId!, 1);
		} else if (event.key === "ArrowUp") {
			event.preventDefault();
			newSelectedId = findItemAfterId(visibleItems, selectedId!, -1);
		}
		if (newSelectedId) {
			let indexOfItem = focusThisItemById(newSelectedId);
			if (virtualListElement) {
				const nthItem = virtualListElement.querySelector(
					`[data-index="${indexOfItem}"]`
				);
				if (nthItem) {
					nthItem.scrollIntoView({
						behavior: "smooth",
						block: "nearest",
						inline: "start"
					});
				}
			}
		}
	};

	const focusThisItemById = (id: string) => {
		let index = 0;
		for (let i = 0; i < items.length; i++) {
			if (items[i].id === id) {
				items[i].focus = true;
				index = i;
			} else {
				items[i].focus = false;
			}
		}
		return index;
	};

	const focusThisItem = (index: number) => {
		items = items.map((x) => ({
			...x,
			focus: x.index === index && !x.disabled
		}));
	};

	const updateSearchText = async (query: string) => {
		if (isRemote && remoteSearch) {
			await debouncedFetchMoreOptionsFn(true);
		}
		if (!isRemote || (isRemote && !remoteSearch)) {
			highlightText = query;
			if (!query || !index) {
				items = items.map((x) => ({ ...x, visible: true }));
				return;
			}
			let search = index.search(query);
			if (query.trim() === "") {
				items = items.map((x) => ({ ...x, visible: true }));
			} else {
				items = items.map((x) => ({ ...x, visible: search.includes(x.label) }));
			}
			if (!items.find((x) => x.visible && x.focus)) focusFirstItem();
			await tick();
			if (scrollToTop) scrollToTop();
		}
	};

	const focusFirstItem = () => {
		items = items.map((x) => ({ ...x, focus: false }));
		for (let item of items) {
			if (item.visible) {
				item.focus = true;
				break;
			}
		}
	};

	const fetchMoreOptionsFn = async (reset = false) => {
		if (optionsFn === null || !BROWSER || !open) return;
		remoteLoading++;
		if (items.length === 0) {
			reset = true;
		}
		let skip: null | number = reset ? 0 : items.length;
		let take: null | number = remoteTake;
		if (!remotePagination) {
			skip = null;
			take = null;
		}
		let temp;
		try {
			temp = await optionsFn(skip, take, searchText);
		} catch (e) {
			remoteLoading--;
			return;
		}
		if (!temp) {
			remoteLoading--;
			return;
		}
		initialFetch = true;
		highlightText = searchText;
		remoteLoading--;
		if (reset || remotePagination === false) {
			if (typeof scrollToTop === "function") scrollToTop();
			optionsFromFunction = [...temp.data];
			infinite?.reset();
		} else {
			let formsCopy = [...optionsFromFunction];
			optionsFromFunction = formsCopy
				.slice(0, items.length)
				.concat(temp.data, formsCopy.slice(items.length));
		}
		await tick();
		if (
			temp.complete ||
			temp.data.length < remoteTake ||
			(temp.total !== null && temp.total > 0 && temp.total <= items.length)
		) {
			infinite?.complete();
		} else {
			infinite?.loaded();
		}
		if (isNumeric(temp.total)) {
			remoteTotal = temp.total;
		} else {
			remoteTotal = null;
		}
		if (temp.error) {
			// Reset the options
			optionsFromFunction = [];
			initialFetch = false;
			infinite?.reset();
		}
	};

	const debouncedFetchMoreOptionsFn = debounce(
		fetchMoreOptionsFn,
		500,
		(state) => {
			willLoad = state;
		}
	);

	export const openListBox = async () => {
		if (open) return;
		open = true;
		if (searchText !== "" || highlightText !== "") {
			searchText = "";
			highlightText = "";
			updateSearchText("");
		}
		await tick();
		if (!positionRef || !listboxRef) return;
		cleanup = autoUpdate(positionRef, listboxRef, async () => {
			if (!positionRef || !listboxRef) return;
			const { x, y } = await computePosition(positionRef, listboxRef, {
				placement: "bottom-start",
				middleware: [
					flip(
						fancyPlacement
							? {
									fallbackPlacements: ["right", "bottom", "top"]
								}
							: undefined
					),
					shift({
						padding: 12
					}),
					sizeModifier({
						apply({ availableWidth, availableHeight, elements }) {
							const maxHeight =
								availableHeight && availableHeight > 100
									? `${availableHeight - 32}px`
									: "80vh";
							Object.assign(elements.floating.style, {
								maxHeight,
								maxWidth:
									maxWidth ||
									`${Math.min(availableWidth, columns > 1 ? 460 : 320)}px`,
								width: fixedWidth
									? `${fixedWidth}px`
									: isRemote
										? "320px"
										: "auto"
							});
						}
					}),
					offsetModifier(offset)
				]
			});
			const { width } = listboxRef.getBoundingClientRect();
			if (width > 200) {
				fixedWidth = width;
			}
			Object.assign(listboxRef.style, {
				left: `${x}px`,
				top: `${y}px`
			});
		});
		await tick();
		if (optionsFn !== null) {
			start = 0;
		} else {
			items = items.map((x) => ({ ...x, focus: false }));
		}
		focusFirstItem();
		if (inputRef) {
			inputRef.addEventListener("keydown", inputRefKeydown, true);
		}
		document.addEventListener("keyup", keyup, true);
		document.addEventListener("keydown", keydown, true);
		document.body.addEventListener("mouseenter", selectItemHover, true);
	};

	const closeListBox = () => {
		if (listboxRef) listboxRef.blur();
		if (inputRef) inputRef.blur();
		open = false;
		if (inputRef) {
			inputRef.removeEventListener("keydown", inputRefKeydown, true);
		}
		document.removeEventListener("keyup", keyup, true);
		document.removeEventListener("keydown", keydown, true);
		document.body.removeEventListener("mouseenter", selectItemHover, true);
		if (cleanup) cleanup();
	};

	function findParentWithAttribute(
		elem: Element,
		attr: string,
		maxLevels = 5
	): Element | null {
		if (maxLevels < 1 || !elem.parentElement) {
			return null;
		}

		if (elem.hasAttribute(attr)) {
			return elem;
		}

		if (elem.parentElement.hasAttribute(attr)) {
			return elem.parentElement;
		}

		return findParentWithAttribute(elem.parentElement, attr, maxLevels - 1);
	}

	const selectItemClick = (event: MouseEvent) => {
		if (!event.target || !(event.target instanceof Element)) return;
		let target = findParentWithAttribute(event.target, "role", 5);
		if (target === null) return;
		const dataIndex = target.getAttribute("data-index");
		const item = items.find((x) => x.index === parseInt(dataIndex!, 10));
		if (item && !item.disabled) {
			searchText = "";
			selectThisItem(item);
		}
	};

	const selectItemHover = (event: MouseEvent) => {
		if (!event.target || !(event.target instanceof Element)) return;
		let target: HTMLElement;
		if (event.target.hasAttribute("role")) target = <HTMLElement>event.target;
		else if ((<HTMLElement>event.target.parentNode).hasAttribute("role"))
			target = <HTMLElement>event.target.parentNode;
		else return;
		const disabled = target.getAttribute("aria-disabled");
		if (target.hasAttribute("data-index") && disabled !== "true")
			focusThisItem(parseInt(target.getAttribute("data-index")!, 10));
	};

	const keydownInput = (event: KeyboardEvent) => {
		if (inputFocus && !open && event.key !== "Tab") {
			openListBox();
		}
		if (event.key === "Backspace" && searchText.length === 0) {
			selectedItems.pop();
			selectedItems = selectedItems;
		}
	};
	const focusInput = () => {
		requestAnimationFrame(() => {
			inputFocus = true;
		});
		document.addEventListener("keydown", keydownInput);
	};
	const blurInput = () => {
		inputFocus = false;
		document.removeEventListener("keydown", keydownInput);
		closeListBox();
	};

	$: showNoResultsDiv =
		items.filter((x) => x.visible).length === 0 && !remoteLoading;

	$: {
		if (max > 1 && selectedItems.length > 0) {
			computedPlaceholder = "...";
		} else if (max === 1 && selectedItems.length === 1) {
			computedPlaceholder = "";
		} else {
			computedPlaceholder = placeholder;
		}
	}
	onMount(() => {
		if (!positionRef) {
			positionRef = selectRef;
		}
		if (optionsFn !== null) {
			isRemote = true;
		}
	});

	onDestroy(() => {
		if (BROWSER) {
			closeListBox();
		}
	});
</script>

<div
	bind:this="{element}"
	class="select-component {$$props.parentClass || ''}"
	class:is-sm="{$$props.class?.includes('sm') && max > 1}">
	{#if label}
		<Label instructions="{instructions}">
			{label}
		</Label>
	{/if}
	<button
		type="button"
		aria-label="{placeholder}"
		title="{placeholder || 'field select'}"
		bind:this="{selectRef}"
		tabindex="{tabindex}"
		on:focusin="{() => {
			if (inputRef) {
				inputRef.focus();
			}
			openListBox();
		}}"
		on:click="{() => {
			if (inputRef) {
				inputRef.focus();
			}
		}}"
		on:blur="{blurInput}"
		on:mousedown="{openListBox}"
		on:mouseenter="{() => (hasHover = true)}"
		on:mouseleave="{() => (hasHover = false)}"
		class:focus="{inputFocus}"
		class:outline-none="{inputFocus}"
		class="select-component-button main-button {$$props.class || ''}">
		<div class="select-component-button-inner">
			{#each selectedItems as item, itemIndex}
				<div
					class:box="{max > 1}"
					style="margin-right: {max > 1 ? '0.25rem' : ''}; opacity: {max ===
						1 &&
					inputFocus &&
					searchText !== ''
						? '0'
						: max === 1 && inputFocus
							? '0.30'
							: !hasHover
								? '0.80'
								: ''}"
					class="selected-item">
					<slot
						name="selectedItem"
						item="{item}"
						internalValue="{internalValue}"
						itemIndex="{itemIndex}"
						selectedItems="{selectedItems}">
						<SelectEmbed width="0.95rem" embed="{item.embed}" />
						<span style="display: inline-flex: flex: flex: 1 1 0%">
							{#if item.label}
								{item.label}
							{:else}
								<span style="color: rgb(248, 113, 113)">(invalid)</span>
							{/if}
						</span>
					</slot>
					{#if max > 1}
						<button
							type="button"
							class="remove-item"
							on:click|preventDefault|stopPropagation="{() => {
								selectedItems.splice(itemIndex, 1);
								selectedItems = selectedItems;
							}}">
							<svg
								viewBox="0 0 24 24"
								width="18"
								height="18"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
								><path
									d="M16 8L8 16M8.00001 8L16 16"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"></path
								></svg>
						</button>
					{/if}
				</div>
			{/each}

			{#if !allowSearch && (selectedItems.length === 0 || max > 1)}
				<div
					placeholder="{computedPlaceholder}"
					class:mr-1="{max > 1}"
					bind:this="{inputRef}"
					on:focusin="{focusInput}"
					on:focusout="{blurInput}"
					class="select-input {hasHover || inputFocus ? 'focus' : 'no-focus'}">
				</div>
			{/if}
			{#if allowSearch}
				<div
					placeholder="{computedPlaceholder}"
					class:mr-1="{max > 1}"
					class="select-input {hasHover && !inputFocus
						? 'focus'
						: 'no-focus'} {max === 1 && inputFocus && selectedItems.length !== 0
						? 'absolute'
						: ''}"
					role="textbox"
					contenteditable="true"
					title="{placeholder || 'field select'}"
					aria-label="{placeholder || 'field select'}"
					tabindex="{tabindex}"
					bind:innerText="{searchText}"
					bind:this="{inputRef}"
					on:focusin="{focusInput}"
					on:focusout="{blurInput}">
					<span style="border-radius: inherit; height: 100%"> </span>
				</div>
			{/if}
		</div>
		{#if suffixIcon}
			{#if suffixIcon.type === "svg"}
				{#key suffixIcon.content}
					<svg-load
						height="1.3rem"
						style="display: flex; height: 100%; padding: 0; min-height: 1.3rem"
						src="{getImageSrc(suffixIcon)}"></svg-load>
				{/key}
			{:else if suffixIcon.type === "emoji"}
				<span style="">{suffixIcon.content}</span>
			{:else if suffixIcon.type === "inline_svg"}
				{@html suffixIcon.content}
			{/if}
		{/if}
		{#if chevron && !showMark}
			<div class="chevron-down">
				<svg
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					style="margin-bottom: -1px; margin-left: 2px"
					width="{chevronSize}"
					height="{chevronSize}"
					class="focus:outline-none"
					viewBox="0 0 24 24"
					stroke-width="{chevronSize < 10 ? '2.85' : '2.25'}"
					stroke="currentColor">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						d="M19.5 8.25l-7.5 7.5-7.5-7.5"></path>
				</svg>
			</div>
		{/if}
		{#if showMark}
			<button
				bind:this="{clearButton}"
				class="x-mark"
				on:mousedown|preventDefault|stopPropagation="{() => {
					closeListBox();
					selectedItems = [];
				}}">
				<svg
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					width="12"
					height="12"
					class="focus:outline-none"
					viewBox="0 0 24 24"
					style="margin-bottom: -1px; margin-left: 2px"
					stroke-width="2.5"
					stroke="currentColor">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						d="M6 18L18 6M6 6l12 12"></path>
				</svg>
			</button>
		{/if}
	</button>
	{#if description}
		<p class="text-xs opacity-70 mt-1.5">{description}</p>
	{/if}
</div>
{#if open}
	{#if deep_shadow}
		<div use:portal="{'body'}" class="deep-shadow-portal"></div>
	{/if}
	<div
		transition:subtlepop="{{ duration: animate ? 200 : 0 }}"
		bind:this="{listboxRef}"
		use:portal="{'body'}"
		class:shadow-dropdown-dark="{deep_shadow}"
		style="font-size: {deep_shadow ? '1.25rem' : '.925rem'}; padding: {columns >
		1
			? '0.5rem'
			: '0.25rem 0'}"
		class="select-dropdown-cover shadow-dropdown"
		class:multi-column="{columns > 1}"
		role="listbox"
		tabindex="0"
		class:invisible="{!listVisible}"
		on:keydown="{(e) => e}"
		on:focusin="{() => (listboxFocus = true)}"
		use:mousedownOutside="{{
			callbackFunction: (e) => {
				closeListBox();
			},
			ignoreElements: [selectRef]
		}}"
		on:mousedown|preventDefault|stopPropagation="{(event) =>
			selectItemClick(event)}"
		on:mouseenter="{(event) => selectItemHover(event)}"
		on:focusout="{() => (listboxFocus = false)}">
		<div class="select-dropdown" bind:this="{listboxOptionsRef}">
			{#if remoteLoading}
				<div
					class="loading-cover"
					style="opacity: 0.5; height: {listHeightSimple
						? `${listHeightSimple}px`
						: 'h-content'}">
					{#if items.length === 0}
						<div style="padding: 0.16rem 0.75rem; opacity: 0.5">Loading</div>
					{/if}
				</div>
			{/if}
			{#if groups && groups.length > 1}
				{#each groups.filter((item) => item !== otherGroup) as group, groupIndex (group)}
					{@const last = groupIndex === groups.length - 1}
					{@const first = groupIndex === 0}
					{@const groupItems = items.filter(
						(x) => x.visible && x.group === group
					)}
					{#if groupItems.length > 0}
						{@const is_open =
							openGroups.includes(group) || searchText !== "" || showAllGroups}
						<div
							class:last="{last}"
							class:first="{first}"
							class:border-top="{showAllGroups && !first}"
							class="group-item">
							<button
								type="button"
								on:click="{() => {
									openGroups = openGroups.includes(group)
										? openGroups.filter((x) => x !== group)
										: [...openGroups, group];
								}}"
								class:active="{is_open}"
								style="{showAllGroups ? 'cursor: default' : ''}"
								class="select-item-head">
								<div class="select-item-head-text">
									<span style="font-weight: 560">{group}</span>
									<span class="select-item-head-pill">
										{groupItems.length.toLocaleString()}
									</span>
								</div>
								{#if showAllGroups === false}
									<div class="group-chevron">
										<svg
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											style="transform: rotate({is_open ? '180deg' : '0deg'})"
											width="12"
											height="12"
											class="focus:outline-none"
											viewBox="0 0 24 24"
											stroke-width="2.35"
											stroke="currentColor">
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												d="M19.5 8.25l-7.5 7.5-7.5-7.5"></path>
										</svg>
									</div>
								{/if}
							</button>
							<div
								style="display: grid; grid-template-columns: repeat({columns}, minmax(0, 1fr)); {is_open
									? ''
									: 'height: 0px; overflow: hidden'}">
								{#each groupItems.filter((x) => x.visible) as item (item.id)}
									{@const selected = Boolean(
										selectedItems.find((x) => x.id === item.id)
									)}
									<SelectItem
										textSize="{textSize}"
										item="{item}"
										truncate="{truncate}"
										searchText="{highlightText}"
										paddingLeft="{false}"
										hasDescription="{hasDescription}"
										hasGroups="{true}"
										multiColumn="{columns > 1}"
										selected="{selected}" />
								{/each}
							</div>
						</div>
					{/if}
				{/each}
				{#each items.filter((x) => x.group === otherGroup && x.visible) as item}
					{@const selected = Boolean(
						selectedItems.find((x) => x.id === item.id)
					)}
					<SelectItem
						textSize="{textSize}"
						item="{item}"
						truncate="{truncate}"
						searchText="{highlightText}"
						paddingLeft="{false}"
						hasDescription="{hasDescription}"
						hasGroups="{false}"
						multiColumn="{columns > 1}"
						selected="{selected}" />
				{/each}
			{:else}
				<div
					bind:clientHeight="{listHeightSimple}"
					style="display: flex; flex-direction: column"
					bind:this="{virtualListCover}">
					{#each items.filter((x) => x.visible) as item}
						<SelectItem
							textSize="{textSize}"
							item="{item}"
							truncate="{truncate}"
							searchText="{highlightText}"
							hasDescription="{hasDescription}"
							multiColumn="{columns > 1}"
							hasGroups="{false}"
							selected="{Boolean(
								selectedItems.find((x) => x.id === item.id)
							)}" />
					{/each}
					<InfiniteLoading
						bind:status="{infiniteStatus}"
						on:infinite="{(e) => {
							if (remotePagination || initialFetch === false) {
								const reset = Boolean(e.detail);
								fetchMoreOptionsFn(reset);
							}
						}}"
						on:initialize="{({
							detail: { loaded, complete, reset, error, get }
						}) => (infinite = { loaded, complete, reset, error, get })}"
						style="display: flex; flex-direction: column; flex: 1 1 0%; opacity: 0.5">
						<span slot="spinner"></span>
						<span slot="noResults"></span>
					</InfiniteLoading>
				</div>
			{/if}
			<div
				style="display: {showNoResultsDiv ? 'block' : 'none'}"
				bind:this="{noResultsDiv}">
				<div style="padding: 0.16rem 0.75rem; font-size: 95%;">
					{#if remoteSearch && searchText === ""}
						<span style="opacity: 0.5"
							>{t?.get("type_to_search") ?? "Search"}</span>
					{:else if willLoad}
						<span style="opacity: 0.5"
							>{t?.get("type_to_search") ?? "Search"}</span>
					{:else}
						No results
					{/if}
				</div>
			</div>
		</div>
		{#if footer && items.length > 0}
			<div class="select-dropdown-footer">{@html footer}</div>
		{/if}
	</div>
{/if}
